import React, { useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { Stripe, StripePaymentElementOptions } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import {
  ModalProps,
  PanelHeader,
  Button,
  Icons,
  Loader,
  Modal,
} from "@pushpress/shared-components";
import { useToggle } from "../../../hooks/state";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { PaymentMethod, postSavePaymentMethod } from "../../../apis/billingApi";
import styles from "./PaymentModal.module.scss";

interface PaymentModalProps extends ModalProps {
  stripe: Stripe | PromiseLike<Stripe | null>;
  stripeClientSecret: string;
  userUuid: string;
  title: string;
  stripeCustomer: string;
  onCreate?: (p: PaymentMethod) => void;
}

const PaymentModal = (props: PaymentModalProps) => {
  const { visible, stripeClientSecret, stripe } = props;
  return (
    <Modal visible={visible}>
      <Elements
        stripe={stripe}
        options={{
          clientSecret: stripeClientSecret,
        }}
      >
        <PaymentModalContent {...props} />
      </Elements>
    </Modal>
  );
};

const PaymentModalContent = (props: PaymentModalProps) => {
  const { userUuid, stripeCustomer, title, onCreate, closeModal } = props;

  const { client } = useContext(AppContext);
  const stripe = useStripe();
  const elements = useElements();
  const [elementLoading, loadingElementActions] = useToggle(true);
  const [cardSaving, savingCardActions] = useToggle(false);

  const { mutateAsync: savePaymentMethod } = useMutation(postSavePaymentMethod);

  const saveCard = async () => {
    try {
      savingCardActions.on();
      if (!elements || !stripe) {
        throw new Error("Stripe not found");
      }
      const { setupIntent, error } = await stripe.confirmSetup({
        elements: elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });
      if (error) {
        throw new Error(extractError(error));
      }
      if (!setupIntent) {
        throw new Error("Save payment error");
      }
      const paymentMethod = await savePaymentMethod({
        userUuid: userUuid,
        clientUuid: client!.uuid,
        paymentMethodId: setupIntent && (setupIntent.payment_method as string),
        customerId: stripeCustomer,
      });
      if (!paymentMethod?.billing) {
        throw new Error("Save payment is empty");
      }
      onCreate?.(paymentMethod.billing);
      closeModal?.();
    } catch (e: any) {
      toast.error(extractError(e), defaultToastOptions);
    } finally {
      savingCardActions.off();
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    wallets: {
      applePay: "never",
      googlePay: "never",
    },
  };

  return (
    <div className={styles.container}>
      <PanelHeader title={title}>
        <PanelHeader.ActionMenu>
          <Button
            buttonType="secondary"
            text="Cancel"
            size="medium"
            onClick={closeModal}
          />
          <Button
            buttonType="primary"
            text="Save"
            icon={Icons.Check}
            iconLocation="left"
            size="medium"
            loading={cardSaving}
            disabled={!stripe}
            onClick={saveCard}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div className={styles.content}>
        {elementLoading && <Loader />}
        <PaymentElement
          options={paymentElementOptions}
          onReady={loadingElementActions.off}
        />
      </div>
    </div>
  );
};

export default PaymentModal;
